import {makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IH2hStore, IChallenge} from "data/stores/h2h/h2h.store";
import {OpponentRelationUserType, OpponentUserType, ChallengeStatus} from "data/enums/challenge";
import {ContestStatus, ModalType} from "data/enums";
import type {IContestsStore} from "data/stores/contests/contestsStore";
import {Empty} from "data/types/global";
import {extractErrorMessage} from "data/utils";
import type {IFriendsStore} from "data/stores/friends/friends.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {IRosterStore} from "data/stores/roster/roster.store";

interface IProps {
	challenge: IChallenge;
}

export interface IChallengeGameController extends ViewController<IProps> {
	i18n: ILocalizationStore;
	get isFriend(): boolean;
	get isBot(): boolean;
	get isAccepted(): boolean;
	get isWin(): boolean;
	get contestStatus(): Empty<ContestStatus>;

	onAddFriend: () => void;
}

@injectable()
export class ChallengeGameController implements IChallengeGameController {
	@observable private _challenge?: IChallenge;
	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.H2hStore) public readonly _h2hStore: IH2hStore,
		@inject(Bindings.ContestsStore) public readonly _contestStore: IContestsStore,
		@inject(Bindings.FriendsStore) public readonly _friendsStore: IFriendsStore,
		@inject(Bindings.ModalsStore) public readonly _modalsStore: IModalsStore,
		@inject(Bindings.RosterStore) public readonly _rosterStore: IRosterStore
	) {
		makeAutoObservable(this);
	}

	get isFriend(): boolean {
		return this.challenge?.opponentType === OpponentRelationUserType.FRIEND;
	}

	get isBot(): boolean {
		return this.challenge?.opponent.type === OpponentUserType.BOT;
	}

	get contestStatus(): Empty<ContestStatus> {
		return this._contestStore.currentContest?.status;
	}

	get isAccepted(): boolean {
		return this.challenge?.status === ChallengeStatus.ACCEPTED;
	}

	get isWin(): boolean {
		return false;
	}

	init(param: IProps) {
		this._challenge = param.challenge;
	}

	onChange(param: IProps) {
		this.init(param);
	}

	private get challenge() {
		return this._challenge;
	}

	onAddFriend = () => {
		const friend = this._challenge?.opponent.id;

		if (friend) {
			void this._friendsStore
				.addFriendById(friend)
				.then(() => {
					if (this._challenge) {
						this._challenge.opponentType = OpponentRelationUserType.FRIEND;
					}
				})
				.catch((e) => {
					this._modalsStore.showModal(ModalType.ERROR, {
						message: extractErrorMessage(e),
					});
				});
		}
	};
}
